import { useEffect } from 'react';
import { buildPageUrl, useSiteConfig } from './SiteConfig';

export interface PageMeta {
  title: string;
  description?: string;
  /** Verbatim `<title>` (overrides the `"{title} — {brand}"` template). */
  seoTitle?: string;
  /** Meta description override (defaults to `description`). */
  seoDescription?: string;
  /** When true, emit `noindex, follow` instead of `index, follow`. */
  noindex?: boolean;
  /** Absolute og:image / twitter:image URL. Defaults to the site-wide `/og.png`. */
  image?: string;
  /** Optional structured data injected into a managed `<script>` (or removed). */
  jsonLd?: object | object[] | null;
}

const JSONLD_SELECTOR = 'script[data-byaft-jsonld]';

function upsertMeta(selector: string, create: () => HTMLMetaElement, content: string) {
  let tag = document.head.querySelector<HTMLMetaElement>(selector);
  if (!tag) {
    tag = create();
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = href;
}

function upsertJsonLd(json: string) {
  let el = document.head.querySelector<HTMLScriptElement>(JSONLD_SELECTOR);
  if (!json) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-byaft-jsonld', '');
    document.head.appendChild(el);
  }
  el.textContent = json;
}

/**
 * Keeps an app's <head> in sync per route: title, meta description, canonical,
 * Open Graph / Twitter, the robots tag, and optional structured data. The origin
 * + brand name come from the nearest <SiteConfigProvider>, so the same hook is
 * reused across all three BYAFT sites. The robots tag is always set so navigating
 * from a `noindex` route back to an indexable one re-enables indexing.
 */
export function usePageMeta({
  title,
  description,
  seoTitle,
  seoDescription,
  noindex = false,
  image,
  jsonLd = null,
}: PageMeta) {
  const { siteUrl, brandName } = useSiteConfig();
  const metaDescription = seoDescription ?? description;
  const ogImage = image ?? `${siteUrl}/og.png`;
  const jsonLdStr = jsonLd ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    const fullTitle = seoTitle ?? (title === brandName ? brandName : `${title} — ${brandName}`);
    document.title = fullTitle;

    const url = buildPageUrl(siteUrl, window.location.pathname);
    upsertCanonical(url);
    upsertMeta('meta[property="og:url"]', () => {
      const m = document.createElement('meta');
      m.setAttribute('property', 'og:url');
      return m;
    }, url);
    upsertMeta('meta[property="og:title"]', () => {
      const m = document.createElement('meta');
      m.setAttribute('property', 'og:title');
      return m;
    }, fullTitle);
    upsertMeta('meta[name="twitter:title"]', () => {
      const m = document.createElement('meta');
      m.name = 'twitter:title';
      return m;
    }, fullTitle);

    upsertMeta('meta[name="robots"]', () => {
      const m = document.createElement('meta');
      m.name = 'robots';
      return m;
    }, noindex ? 'noindex, follow' : 'index, follow');

    upsertMeta('meta[property="og:image"]', () => {
      const m = document.createElement('meta');
      m.setAttribute('property', 'og:image');
      return m;
    }, ogImage);
    upsertMeta('meta[name="twitter:image"]', () => {
      const m = document.createElement('meta');
      m.name = 'twitter:image';
      return m;
    }, ogImage);

    if (metaDescription) {
      upsertMeta('meta[name="description"]', () => {
        const m = document.createElement('meta');
        m.name = 'description';
        return m;
      }, metaDescription);
      upsertMeta('meta[property="og:description"]', () => {
        const m = document.createElement('meta');
        m.setAttribute('property', 'og:description');
        return m;
      }, metaDescription);
      upsertMeta('meta[name="twitter:description"]', () => {
        const m = document.createElement('meta');
        m.name = 'twitter:description';
        return m;
      }, metaDescription);
    }

    upsertJsonLd(jsonLdStr);
  }, [title, seoTitle, metaDescription, noindex, ogImage, jsonLdStr, siteUrl, brandName]);
}
